<template>
  <DialogForm v-model:visible="visible" :form :loading placement="left" :reset-on-dialog-close="false" :submit-text="$t('submit')" :title="$t('malfunction')" @submit="submit">
    <p class="font-medium" style="margin-bottom: 15px">{{ $t('use-this-form-to-report-problems') }}</p>

    <VTextarea v-model="form.description" :label="$t('description')" :maxlength="2000" prop="description" :rows="4" />

    <VTextarea v-if="$env.app_env !== 'production'" v-model="form.reproducible_steps" :label="$t('reproducible-steps')" :maxlength="2000" prop="reproducible_steps" :rows="4" />

    <VSelect v-model="form.category" filterable :label="$t('category')" @update:model-value="categoryChangedByUser = true">
      <VOption v-for="(value, label) in categories" :key="value" :label :value />
    </VSelect>

    <VSelect v-model="form.follow_up_email" :label="$t('may-we-follow-up-on-the-given-feedback-via-email')">
      <VOption v-for="item in options" :key="item.label" :label="item.label" :value="item.value" />
    </VSelect>

    <VUpload :accept="$env.file_upload_all_extensions" drag :files="form.files" :max="5" multiple :pastable="visible" style="display: inline" @update:files="updateFiles" />
  </DialogForm>
</template>

<script setup lang="ts">
const visible = defineModel<boolean>('visible')

const loading = ref(false)
const categoryChangedByUser = ref(false)
const options = [
  { label: $t('yes'), value: true },
  { label: $t('no'), value: false },
]

const form = useForm(
  {
    description: '',
    reproducible_steps: '',
    category: categories[document.title.replace(` - ${$env.app_name}`, '')] ?? '',
    follow_up_email: true,
    files: [] as File[],
  },
  {
    rules: {
      description: { required: true, max: 2000, message: $t('please-describe-the-bug') },
      reproducible_steps: { required: $env.app_env !== 'production', max: 2000, message: $t('tell-us-how-we-can-reproduce-the-problem') },
    },
  },
)

watch(visible, (visible) => {
  if (visible && !categoryChangedByUser.value) {
    form.category = categories[document.title.replace(` - ${$env.app_name}`, '')] ?? ''
  }
})

function updateFiles(files: File[]) {
  if (files.some((file) => file.size > 2000000)) {
    $message.error('File is too large. The maximum size is 2MB')

    return
  }

  form.files = files
}

async function submit() {
  loading.value = true

  const payload = {
    ...form.data(),
    title: form.description.split('\n')[0].split('. ')[0].slice(0, 180),
    url: window.location.origin + window.location.pathname + window.location.search + window.location.hash,
    viewport_width: String(window.innerWidth),
    viewport_height: String(window.innerHeight),
  }

  try {
    await api.postForm('malfunction-report', payload)
    categoryChangedByUser.value = false
    form.reset()
    $message.success($t('your-report-was-successfully-submitted'))
  } catch {
    $message.error($t('api-error'))
  }

  loading.value = false
}
</script>

<script lang="ts">
export const categories: Record<string, string> = {
  Account: 'Account',
  Statistics: 'Statistics',
  Billing: 'Billing',
  Case: 'Case dashboard',
  Cases: 'Cases',
  Clients: 'Clients',
  Confidence: 'Confidence',
  Configuration: 'Settings',
  Home: 'Home',
  'Data Directory': 'Data directory',
  Groups: 'Groups',
  Importing: 'Importing',
  Login: 'Login',
  Monitoring: 'Monitoring',
  Navigation: 'Navigation',
  Notifications: 'Notifications',
  Policies: 'Policies',
  Reports: 'Reports',
  Resolving: 'Resolving',
  'Risk Classification': 'Risk',
  Searches: 'Searches',
  Support: 'Support',
  Users: 'Users',
  General: 'General',
}
</script>
